import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { authApi } from '../services/api'
import { mediaUrl } from '../services/config'
import { useAuthStore } from '../stores/authStore'

/**
 * Magic-link activation page — Phase 2 onboarding endpoint.
 *
 * Lands here from the invite email (/activate?token=…). We hit
 * /api/auth/activate-info to validate the token and pre-fill the
 * user's name + email, then ask for a password. On submit we POST
 * /api/auth/activate, which sets the password, flips status to
 * ACTIVE, and returns a JWT — we drop it into the auth store the
 * same way the manual /login flow does so the user lands on / fully
 * signed in (no second login round-trip).
 */
export default function Activate() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const setUser = useAuthStore(s => s)
  const token = searchParams.get('token') || ''

  const [loading, setLoading] = useState(true)
  const [info, setInfo] = useState<{
    email: string
    firstName: string
    lastName: string | null
    position: string | null
    avatarUrl: string | null
    accountStatus: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('Missing token')
      setLoading(false)
      return
    }
    authApi
      .activateInfo(token)
      .then(res => setInfo(res.data.user))
      .catch(e => setError(e.response?.data?.error || 'Invalid or expired link'))
      .finally(() => setLoading(false))
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      const res = await authApi.activate({ token, password })
      localStorage.setItem('token', res.data.token)
      // Reuse the auth store's setter rather than calling login() again
      // — we already have a fresh JWT + user payload, no need for a
      // second /auth/me round-trip.
      ;(setUser as any).setState?.({ user: res.data.user, isAuthenticated: true })
      navigate('/')
    } catch (e: any) {
      setError(e.response?.data?.error || 'Activation failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 shadow-lg">
        {loading ? (
          <div className="text-center text-muted-foreground text-sm py-6">
            <Loader2 size={20} className="inline animate-spin mr-2" /> Validating link…
          </div>
        ) : error && !info ? (
          <div>
            <h1 className="text-xl font-bold text-foreground mb-2">Link is no longer valid</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <p className="text-xs text-muted-foreground mt-4">
              Ask your admin to send a fresh invite.
            </p>
          </div>
        ) : info && info.accountStatus === 'ACTIVE' ? (
          <div>
            <h1 className="text-xl font-bold text-foreground mb-2">Already activated</h1>
            <p className="text-sm text-muted-foreground">
              Your account is already active — sign in with your password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 px-4 py-2 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition"
            >
              Go to login
            </button>
          </div>
        ) : info ? (
          <>
            <div className="flex flex-col items-center text-center mb-6">
              {info.avatarUrl && (
                <img
                  src={mediaUrl(info.avatarUrl)}
                  alt=""
                  className="w-16 h-16 rounded-full object-cover ring-2 ring-border mb-3"
                />
              )}
              <h1 className="text-xl font-bold text-foreground">
                Welcome, {info.firstName || info.email}!
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                Set a password to finish setting up your Rx2Go account.
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">{info.email}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    minLength={6}
                    autoFocus
                    required
                    className="w-full bg-card text-foreground rounded px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">
                  Confirm password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  minLength={6}
                  required
                  className="w-full bg-card text-foreground rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary border border-border"
                />
              </div>
              {error && <p className="text-destructive text-sm">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2.5 rounded transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Activate account
              </button>
            </form>
          </>
        ) : null}
      </div>
    </div>
  )
}
